import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import useLibraryBlockedDialog from "@/hooks/useLibraryBlockedDialog";
import { handoutDownloadStart, mixpanelEvent, popupButtonClicked, popupViewed } from "@/lib/mixpanels/mixpanel";
import { Button, CheckboxLabel, Dialog, DialogTitle, TextField, Typography } from "@bookips/sds";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { getLibraryDownloadUrl } from "src/api/library";
import { postMembershipLog } from "src/api/membership";
import { useDialogStore } from "src/store/useDialogStore";
import useSnackbar from "src/store/useSnackbar";
import { getDownloadFileName, getFileExtension } from "../Product/Handout/utils/getDownloadFileName";
// 다이얼로그 ID 상수
const WATERMARK_TIMEOUT_DIALOG_ID = "download-watermark-timeout";
const UNAVAILABLE_BROWSER_DIALOG_ID = "download-unavailable-browser";
const MAX_LOGO_TEXT_LENGTH = 20;
// 다이얼로그 메시지 설정
const dialogMessages = {
    waterMarkTimeout: {
        title: "다운로드 준비 중이에요.",
        description: "파일 용량이 큰 자료는 최대 5분 이상 소요될 수 있어요."
    },
    // 네이버 인앱 브라우저의 경우 다운로드를 보안상 막기 때문에 필요한 다이얼로그 - text는 수정 될 수 있습니다. 그때 주석을 제거하겠습니다. 25.04.04
    unavailableBrowser: {
        title: "PDF 다운로드가 지원되지 않는 환경이에요.",
        confirmText: "확인",
        description: "크롬, 삼성 인터넷, 사파리 등 다른 브라우저에서 다시 시도해주세요."
    }
};
const DownloadDialog = (param)=>{
    let { isOpen , close , selectedData , selectedFileType , onDownloadComplete , getOpenViewer , skipConfirm =false , originalFileName , position ="자료보관함"  } = param;
    const [checked, setChecked] = useState(false);
    const [logoText, setText] = useState("");
    const [enabled, setEnabled] = useState(false);
    const { open: openDialog , close: closeDialog , dialogs  } = useDialogStore();
    const { addSnackbar  } = useSnackbar();
    const openBlockedDialog = useLibraryBlockedDialog();
    // waterMarkTimeout 다이얼로그 열림 여부 체크
    const isWatermarkDialogOpen = !!dialogs[WATERMARK_TIMEOUT_DIALOG_ID];
    // 브라우저 체크 함수 (네이버/카카오 인앱 브라우저 차단)
    const checkBrowser = useCallback(()=>{
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("naver") || userAgent.includes("kakao")) {
            // mx-414: Popup Viewed
            popupViewed({
                Position: position,
                Type: "다운로드 불가 안내(이유:모바일>네이버/카카오인앱)",
                Title: dialogMessages.unavailableBrowser.title,
                Body: dialogMessages.unavailableBrowser.description
            });
            openDialog({
                id: UNAVAILABLE_BROWSER_DIALOG_ID,
                type: "confirm",
                props: {
                    ...dialogMessages.unavailableBrowser,
                    severity: "error",
                    onConfirm: ()=>{
                        // mx-415: Popup Button Clicked
                        popupButtonClicked({
                            Position: position,
                            Type: "다운로드 불가 안내(이유:모바일>네이버/카카오인앱)",
                            Title: dialogMessages.unavailableBrowser.title,
                            Body: dialogMessages.unavailableBrowser.description,
                            "Button Name": "확인",
                            "Button Url": null
                        });
                        close();
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            closeDialog(UNAVAILABLE_BROWSER_DIALOG_ID);
                        }
                    }
                }
            });
            return false;
        }
        return true;
    }, [
        openDialog,
        closeDialog,
        close,
        position
    ]);
    // skipConfirm이 true이고 다이얼로그가 열리면 브라우저 체크 후 다운로드 시작
    useEffect(()=>{
        if (skipConfirm && isOpen && !enabled) {
            if (checkBrowser()) {
                setEnabled(true);
            }
        }
    }, [
        skipConfirm,
        isOpen,
        enabled,
        checkBrowser
    ]);
    const mutateMembershipLog = useMutation(postMembershipLog);
    const getDownloadUrlMutation = useMutation(getLibraryDownloadUrl, {
        onMutate: ()=>{
            if (!isWatermarkDialogOpen) {
                openDialog({
                    id: WATERMARK_TIMEOUT_DIALOG_ID,
                    type: "custom",
                    props: {
                        title: /*#__PURE__*/ _jsx("div", {
                            className: "pb-7",
                            children: /*#__PURE__*/ _jsx(DialogTitle, {
                                title: dialogMessages.waterMarkTimeout.title,
                                description: dialogMessages.waterMarkTimeout.description
                            })
                        }),
                        contentProps: {
                            onInteractOutside: ()=>{
                                closeDialog(WATERMARK_TIMEOUT_DIALOG_ID);
                            }
                        },
                        classes: {
                            contentWrapper: "w-[calc(100%-40px)] md:w-[520px]"
                        }
                    }
                });
            }
        }
    });
    const downloadFile = async (url, filename)=>{
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error("Failed to download file");
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };
    //다운로드 링크 조회
    useQuery([
        "getDownloadUrl",
        selectedData.id
    ], async ()=>{
        const result = await getDownloadUrlMutation.mutateAsync({
            id: selectedData.id,
            logo: logoText,
            ...selectedData.subItemId ? {
                subItemId: selectedData.subItemId
            } : {}
        });
        try {
            // 원본파일 다운로드
            if (selectedFileType === "origin" && result.originalFileUrl && selectedData.isOriginSellable) {
                await downloadFile(result.originalFileUrl, "".concat(selectedData.title, ".").concat(getFileExtension(result.originalFileUrl)));
            } else {
                // pdf파일 다운로드
                await downloadFile(result.url, "".concat(selectedData.title, ".pdf"));
            }
            onDownloadComplete(true);
        } catch (error) {
            onDownloadComplete(false);
        }
        return null;
    }, {
        enabled: enabled,
        refetchOnWindowFocus: false,
        // 4xx(구독 무효 403 등)는 재시도해도 결과가 같다. 기존 10회를 그대로 두면
        // "다운로드 준비 중" 다이얼로그를 띄운 채 11번 호출한 뒤에야 안내가 뜬다.
        retry: (failureCount, error)=>{
            var _error_response;
            const status = isAxiosError(error) ? (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status : undefined;
            if (status && status >= 400 && status < 500) return false;
            return failureCount < 10;
        },
        onSuccess: async ()=>{
            // mx-411: Handout Download Start
            handoutDownloadStart({
                Position: position,
                "Product Id": selectedData.productId || null,
                "Product Title": selectedData.title,
                "License Id": selectedData.id || null,
                "Author Id": selectedData.authorId || null,
                "Author Name": selectedData.authorName || "",
                "License Version": selectedData.licenseVersion || null
            });
            mutateMembershipLog.mutate({
                itemId: selectedData.sourceId,
                itemType: selectedData.sourceType,
                membershipId: selectedData.membershipId,
                actionType: "download"
            });
            closeDialog(WATERMARK_TIMEOUT_DIALOG_ID);
            close();
            addSnackbar({
                message: "다운로드를 시작했어요.",
                variant: "success"
            });
        },
        onError: (error)=>{
            var _error_response;
            ;
            closeDialog(WATERMARK_TIMEOUT_DIALOG_ID);
            close();
            // 403 은 구독 무효 등으로 이용이 막힌 경우. 안내하지 않으면 로딩만 사라져 무반응으로 보인다.
            if (isAxiosError(error) && ((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 403) {
                var _selectedData_productId, _ref;
                // MX-646 / MX-647
                openBlockedDialog(error, {
                    position,
                    productId: (_ref = (_selectedData_productId = selectedData.productId) !== null && _selectedData_productId !== void 0 ? _selectedData_productId : selectedData.id) !== null && _ref !== void 0 ? _ref : undefined
                });
                return;
            }
            // getDownloadUrl 자체가 실패하면 onDownloadComplete(false) 가 호출되지 않아
            // 호출부의 "다시 시도해주세요" 스낵바가 뜨지 않는다. 여기서 직접 띄운다.
            addSnackbar({
                message: "다시 시도해주세요.",
                variant: "error"
            });
        }
    });
    const handleDownloadClick = ()=>{
        if (!checkBrowser()) return;
        setEnabled(true);
    };
    return /*#__PURE__*/ _jsxs(Dialog, {
        open: isOpen && !skipConfirm && !enabled,
        onOpenChange: (open)=>{
            if (!open) close();
        },
        classes: {
            contentWrapper: "flex flex-col w-[520px] max-w-[calc(100vw-40px)]"
        },
        title: /*#__PURE__*/ _jsx(DialogTitle, {
            title: "".concat(getDownloadFileName(selectedFileType === "origin", originalFileName), " 파일을 다운로드 할까요?"),
            description: (getOpenViewer === null || getOpenViewer === void 0 ? void 0 : getOpenViewer.isFirstUse) ? "다운로드 후에는 자료를 환불 및 교환할 수 없어요." : undefined
        }),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-col gap-4 px-7 max-sm:px-5",
                children: selectedData.canEmbedLogo && selectedFileType === "pdf" && /*#__PURE__*/ _jsxs("div", {
                    className: "rounded-md bg-gray-50 p-4",
                    children: [
                        /*#__PURE__*/ _jsx(CheckboxLabel, {
                            checked: checked,
                            onCheckedChange: (value)=>{
                                setChecked(Boolean(value));
                                if (value) {
                                    //mx-9
                                    mixpanelEvent("Popup Button Clicked", {
                                        "Popup Type": "PDF Download",
                                        "Popup Title": "PDF 파일을 다운로드 할까요?",
                                        "Button Name": "학원 이름 넣기",
                                        "Button URL": "null"
                                    });
                                }
                            },
                            children: "학원 이름 넣기"
                        }),
                        checked && /*#__PURE__*/ _jsxs("div", {
                            className: "mt-3 flex flex-col gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex gap-4 max-md:flex-col max-md:items-center max-md:gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsxs(Typography, {
                                            variant: "body1",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-gray-900",
                                                    children: "자료의 모든 페이지 우측 상단"
                                                }),
                                                "에 학원 이름을 넣을 수 있어요."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Image, {
                                            src: "/static/images/name-chip-logo.png",
                                            width: 114,
                                            height: 160,
                                            alt: "학원 로고 위치 안내 이미지"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(TextField, {
                                            placeholder: "20자 이내로 입력해주세요.",
                                            size: "lg",
                                            className: "w-full",
                                            value: logoText,
                                            onChange: (e)=>setText(e.target.value),
                                            maxLength: MAX_LOGO_TEXT_LENGTH
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "body2",
                                            className: "mt-1 text-right text-gray-500",
                                            children: "".concat(logoText.length, "/").concat(MAX_LOGO_TEXT_LENGTH)
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex shrink-0 justify-end gap-2 px-7 pt-5 pb-7 max-sm:w-full max-sm:px-5 max-sm:[&>button]:w-full",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "fill",
                        color: "secondary",
                        size: "lg",
                        onClick: close,
                        children: "취소"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "fill",
                        color: "primary",
                        size: "lg",
                        onClick: ()=>{
                            handleDownloadClick();
                            //mx-224
                            mixpanelEvent("PDF Download Button Clicked", {
                                "Button Name": "다운로드",
                                "Logo Included": checked,
                                "Logo Text": logoText
                            });
                        },
                        children: "다운로드"
                    })
                ]
            })
        ]
    });
};
export default DownloadDialog;
